import React, { useState, useEffect } from 'react';
import { X, FileText, Brain, Target, Award, Calendar, Loader2, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * PROV.2: Capability Evidence Drawer
 * Shows the provenance evidence trail for a specific capability domain.
 * Opens as a slide-over panel from the right.
 */

const EVENT_TYPE_CONFIG = {
  reflection: { icon: Brain, label: 'Reflection', color: '#818cf8' },
  situation: { icon: Target, label: 'Situation', color: '#f59e0b' },
  accomplishment: { icon: Award, label: 'Accomplishment', color: '#22c55e' },
  capability: { icon: FileText, label: 'Capability Extract', color: '#06b6d4' },
  calendar: { icon: Calendar, label: 'Calendar Event', color: '#ec4899' },
};

const CapabilityEvidenceDrawer = ({ domain, isOpen, onClose }) => {
  const [evidence, setEvidence] = useState(null);
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!isOpen || !domain) return;

    const fetchEvidence = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch(
          `${API_URL}/api/provenance/capability/${encodeURIComponent(domain)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.ok) {
          const data = await res.json();
          setEvidence(data);
        }
      } catch (err) {
        console.debug('Evidence fetch failed:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvidence();
  }, [isOpen, domain, API_URL]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="evidence-drawer-backdrop"
        className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        data-testid="evidence-drawer"
        className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-gray-950 border-l border-gray-800 z-50 overflow-y-auto shadow-2xl"
      >
        {/* Header */}
        <div className="sticky top-0 bg-gray-950/95 backdrop-blur border-b border-gray-800 px-5 py-4 z-10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Evidence Trail</p>
              <h2 data-testid="evidence-drawer-title" className="text-lg font-bold text-white">{domain}</h2>
            </div>
            <Button
              data-testid="evidence-drawer-close"
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          )}

          {!loading && evidence && (
            <>
              {/* Stats bar */}
              <div data-testid="evidence-stats" className="grid grid-cols-3 gap-3 mb-6">
                <div className="text-center p-3 bg-gray-900/50 border border-gray-800 rounded-lg">
                  <p className="text-xl font-bold text-white">{evidence.event_count}</p>
                  <p className="text-xs text-gray-500">Evidence Points</p>
                </div>
                <div className="text-center p-3 bg-gray-900/50 border border-gray-800 rounded-lg">
                  <p className="text-xl font-bold text-white">{evidence.days_span || 0}</p>
                  <p className="text-xs text-gray-500">Days Span</p>
                </div>
                <div className="text-center p-3 bg-gray-900/50 border border-gray-800 rounded-lg">
                  <p className="text-xs text-white font-medium">{evidence.first_evidence || '—'}</p>
                  <p className="text-xs text-gray-500">First Seen</p>
                </div>
              </div>

              {/* Timeline */}
              {evidence.recent_events && evidence.recent_events.length > 0 ? (
                <div className="space-y-1">
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">Evidence Timeline</p>
                  {evidence.recent_events.map((evt, i) => {
                    const config = EVENT_TYPE_CONFIG[evt.event_type] || EVENT_TYPE_CONFIG.reflection;
                    const Icon = config.icon;
                    return (
                      <div
                        key={evt.event_id || i}
                        data-testid={`evidence-event-${i}`}
                        className="flex gap-3 p-3 bg-gray-900/30 border border-gray-800/50 rounded-lg hover:border-gray-700 transition-colors"
                      >
                        {/* Timeline dot */}
                        <div className="flex flex-col items-center flex-shrink-0">
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${config.color}15` }}
                          >
                            <Icon className="w-4 h-4" style={{ color: config.color }} />
                          </div>
                          {i < evidence.recent_events.length - 1 && (
                            <div className="w-px h-full bg-gray-800 mt-1" />
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium" style={{ color: config.color }}>
                              {config.label}
                            </span>
                            <span className="text-xs text-gray-500">{evt.date}</span>
                          </div>
                          {evt.excerpt ? (
                            <p className="text-sm text-gray-300 leading-relaxed line-clamp-3">
                              {evt.excerpt}
                            </p>
                          ) : (
                            <p className="text-sm text-gray-500 italic">Content recorded</p>
                          )}
                          {evt.capabilities && evt.capabilities.length > 1 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {evt.capabilities.filter(c => c !== domain).slice(0, 3).map((cap, j) => (
                                <span key={j} className="text-xs px-1.5 py-0.5 bg-gray-800 text-gray-400 rounded">
                                  {cap}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-8 h-8 mx-auto mb-3 text-gray-600" />
                  <p className="text-gray-400 text-sm">No evidence events yet for this domain.</p>
                  <p className="text-gray-500 text-xs mt-1">Evidence builds as you reflect, log situations, and track accomplishments.</p>
                </div>
              )}

              {/* Footer explanation */}
              <div className="mt-6 p-3 bg-gray-900/30 border border-gray-800/50 rounded-lg">
                <p className="text-xs text-gray-500 leading-relaxed">
                  Each entry is hash-linked to the previous, forming a tamper-evident chain. 
                  This evidence trail proves this capability emerged from genuine activity — not AI fabrication.
                </p>
              </div>
            </>
          )}

          {!loading && evidence && evidence.event_count === 0 && (
            <div className="text-center py-12">
              <FileText className="w-8 h-8 mx-auto mb-3 text-gray-600" />
              <p className="text-gray-400 text-sm">No evidence for "{domain}" yet.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CapabilityEvidenceDrawer;
